/**
 * Impact Analyzer (CF07) — CL03.
 *
 * Tre gruppi, e la distinzione non e' cosmetica:
 *
 * - **bloccanti** — nessun consenso le salva. Non hanno casella da spuntare
 *   perche' non c'e' niente da accettare: si corregge la configurazione.
 * - **confermabili** — un avviso che il progettista puo' accettare
 *   consapevolmente, e la casella *e'* il consenso.
 * - **informative** — si mostrano, non si spuntano. Una casella su cio' che non
 *   richiede consenso insegnerebbe a spuntare senza leggere.
 *
 * La regola che tiene insieme il resto sta in `canConfirm`: il pulsante e'
 * spento se resta un'anomalia da verificare, e **sempre** se ce n'e' una
 * bloccante. Offrire la conferma su una configurazione che lascerebbe un
 * processo non chiudibile sarebbe una bugia con una casella accanto.
 *
 * Un'analisi scaduta si dice e si blocca: descrive un modello che potrebbe non
 * esistere piu'.
 */

import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  FormControlLabel,
  Stack,
  Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import React, { useEffect, useMemo, useState } from "react";
import DialogHeader from "components/dialogs/DialogHeader";
import { getTranslation } from "common";
import { apiError } from "api/customFieldsApi";
import { messageKeyFor } from "./errorMessages";
import { useTranslations } from "hooks";
import type { FieldDefinition, ImpactAnomaly, ImpactResult } from "model/CustomFields";
import {
  blockingAnomalies,
  canConfirm,
  confirmableAnomalies,
  informationalAnomalies,
  isStale,
  pendingAcknowledgements,
  tokensForSave,
} from "./visibilityImpact";

interface ImpactDialogProps {
  open: boolean;
  onClose: () => void;
  field: FieldDefinition | null;
  impact: ImpactResult | null;
  loading?: boolean;
  /**
   * I campi dello schema in lavorazione, per citarli col **nome**.
   *
   * Le anomalie portano l'id del campo nei loro parametri (`fieldId`, e per le
   * dipendenze `missing`/`source`): un id a schermo non dice niente a chi
   * progetta. Chi apre questa dialog ha in mano la bozza che sta salvando, e
   * quella è la label che il progettista ha appena letto.
   */
  fields?: readonly FieldDefinition[];
  /**
   * Il rifiuto, **intero**: non la sua stringa.
   *
   * `String(e)` produce "AxiosError: Request failed with status code 503": una
   * frase da chi legge il codice, non da chi usa l'applicazione. La chiave di
   * catalogo si ricava dal codice del contratto (`messageKeyFor`), e quando non
   * c'e' un codice resta la voce generica — meglio di un messaggio tecnico.
   */
  error?: unknown;
  onConfirm: (tokens: { impactToken?: string; confirmationToken?: string }) => void;
}

const ImpactDialog: React.FC<ImpactDialogProps> = ({
  open,
  onClose,
  field,
  impact,
  loading = false,
  fields,
  error = null,
  onConfirm,
}) => {
  const translations = useTranslations();
  const [acknowledged, setAcknowledged] = useState<Set<string>>(new Set());

  // Il consenso vale per l'analisi che si sta guardando: riaprendo su un'altra
  // analisi, le spunte di quella precedente non valgono piu'.
  useEffect(() => {
    setAcknowledged(new Set());
  }, [impact?.impactToken, open]);

  const blocking = useMemo(
    () => (impact ? blockingAnomalies(impact) : []),
    [impact]
  );
  const confirmable = useMemo(
    () => (impact ? confirmableAnomalies(impact) : []),
    [impact]
  );
  const informational = useMemo(
    () => (impact ? informationalAnomalies(impact) : []),
    [impact]
  );
  const pending = useMemo(
    () => (impact ? pendingAcknowledgements(impact, acknowledged) : []),
    [impact, acknowledged]
  );

  const stale = impact ? isStale(impact.expiresAt, new Date()) : false;
  const completo = canConfirm(impact, acknowledged);

  const toggle = (anomaly: ImpactAnomaly, checked: boolean) => {
    setAcknowledged((prev) => {
      const next = new Set(prev);
      if (checked) next.add(anomaly.id);
      else next.delete(anomaly.id);
      return next;
    });
  };

  /**
   * Il valore di un parametro, col nome del campo quando è un campo.
   *
   * La risoluzione è **per valore**: qualunque chiave porti un id che lo schema
   * conosce — `fieldId`, e per le dipendenze `missing`/`source` — diventa la
   * label. Un id che non si risolve resta com'è: nasconderlo sarebbe peggio.
   */
  const valueOf = (value: unknown): string => {
    const raw = String(value);
    const noto = fields?.find((candidato) => candidato.fieldId === raw);
    return noto ? noto.label : raw;
  };

  /** Il testo di un'anomalia: la chiave, o i parametri che il catalogo compone. */
  const textOf = (anomaly: ImpactAnomaly) => {
    const base = getTranslation(translations, anomaly.messageKey);
    const params = Object.entries(anomaly.params);
    if (params.length === 0) return base;
    return `${base} (${params.map(([k, v]) => `${k}: ${valueOf(v)}`).join(", ")})`;
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth id="field-impact-dialog">
      <DialogHeader sx={{ px: 4 }} onClose={onClose}>
        {getTranslation(translations, "custom_fields.impact.title")}
      </DialogHeader>
      <DialogContent>
        <Stack gap={2} pt={1}>
          {field && (
            <Typography variant="subtitle2" color="text.secondary">
              {field.label}
            </Typography>
          )}

          {loading && (
            <Stack alignItems="center" py={2}>
              <CircularProgress size={24} data-testid="cf-impact-loading" />
            </Stack>
          )}

          {error ? (
            <Alert severity="error" data-testid="cf-impact-error">
              {getTranslation(translations, messageKeyFor(apiError(error)))}
            </Alert>
          ) : null}

          {impact && stale && (
            // Un'analisi vecchia descrive un modello che potrebbe non esistere
            // piu': il consenso va chiesto su quella corrente.
            <Alert severity="warning" data-testid="cf-impact-stale">
              {getTranslation(translations, "custom_fields.impact.stale")}
            </Alert>
          )}

          {impact && (
            <>
              {blocking.map((anomaly) => (
                <Alert
                  key={anomaly.id}
                  severity="error"
                  data-testid={`cf-impact-blocking-${anomaly.id}`}
                >
                  {textOf(anomaly)}
                </Alert>
              ))}

              {confirmable.map((anomaly) => (
                <FormControlLabel
                  key={anomaly.id}
                  data-testid={`cf-impact-confirmable-${anomaly.id}`}
                  control={
                    <Checkbox
                      size="small"
                      inputProps={
                        {
                          "data-testid": `cf-impact-ack-${anomaly.id}`,
                        } as React.InputHTMLAttributes<HTMLInputElement>
                      }
                      checked={acknowledged.has(anomaly.id)}
                      onChange={(e, checked) => toggle(anomaly, checked)}
                    />
                  }
                  label={textOf(anomaly)}
                />
              ))}

              {informational.map((anomaly) => (
                <Box key={anomaly.id} data-testid={`cf-impact-info-${anomaly.id}`}>
                  {/* Nessuna casella: cio' che non richiede consenso non si
                      spunta, altrimenti si impara a spuntare senza leggere. */}
                  <Typography variant="body2" color="text.secondary">
                    {textOf(anomaly)}
                  </Typography>
                </Box>
              ))}

              {impact.anomalies.length === 0 && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  data-testid="cf-impact-none"
                >
                  {getTranslation(translations, "custom_fields.impact.none")}
                </Typography>
              )}

              {pending.length > 0 && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  data-testid="cf-impact-pending"
                >
                  {getTranslation(translations, "custom_fields.impact.pending")}
                  {": "}
                  {pending.length}
                </Typography>
              )}
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} data-testid="cf-impact-cancel">
          {getTranslation(translations, "custom_fields.action.cancel")}
        </Button>
        <LoadingButton
          disabled={!completo || stale || loading}
          onClick={() => onConfirm(tokensForSave(impact, acknowledged))}
          data-testid="cf-impact-confirm"
        >
          {getTranslation(translations, "custom_fields.impact.confirm")}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default ImpactDialog;
